import React, { useState, useMemo, useCallback, memo } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

const TipButton = memo(
  ({
    tipTypeValue,
    tipsValue,
    displayAmount,
    typeTip,
    setTypeTip,
    environment,
    isOtherButton = false,
    onClick,
  }) => {
    const isSelected = typeTip === tipTypeValue;

    const handleClick = useCallback(() => {
      if (onClick) {
        onClick();
      } else {
        setTypeTip(tipTypeValue);
      }
    }, [onClick, setTypeTip, tipTypeValue]);

    return (
      <Button
        disableRipple={true}
        style={{
          flex: 1,
          padding: '10px 8px',
          borderRadius: '8px',
          backgroundColor: isSelected ? environment.mainColor : '#F6F6F6',
          color: isSelected ? 'white' : '#172B4D',
          textShadow: isSelected ? '0 1px 3px rgba(0, 0, 0, 0.3)' : 'none',
          boxShadow: 'none',
          fontWeight: 600,
          fontSize: '14px',
          fontFamily: 'Jost, sans-serif',
          textTransform: 'none',
          display: 'flex',
          flexDirection: 'column',
          gap: '2px',
          transition: 'all 0.2s ease',
          border: isSelected ? 'none' : '2px solid #E6E6E6',
          minHeight: '56px',
        }}
        variant={'contained'}
        onClick={handleClick}
        onMouseEnter={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = environment.mainColor;
            e.currentTarget.style.transform = 'translateY(-1px)';
          }
        }}
        onMouseLeave={(e) => {
          if (!isSelected) {
            e.currentTarget.style.borderColor = '#E6E6E6';
            e.currentTarget.style.transform = 'translateY(0)';
          }
        }}
      >
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: '15px',
            fontFamily: 'Jost, sans-serif',
          }}
        >
          {tipsValue}
        </Typography>
        {displayAmount && (
          <Typography
            sx={{
              fontSize: '12px',
              fontFamily: 'Jost, sans-serif',
              opacity: 0.8,
            }}
          >
            {displayAmount}
          </Typography>
        )}
      </Button>
    );
  }
);

TipButton.displayName = 'TipButton';

const TipsBox = ({
  typeTip,
  setTypeTip,
  environment,
  total,
  fees,
  t,
  filteredStore,
  apiPricingData,
  setOtherTip, // Function to set custom tip amount
  otherTip, // Current custom tip in cents (to show under "Other" when selected)
}) => {
  const [openCustomTipDialog, setOpenCustomTipDialog] = useState(false);
  const [customTipAmount, setCustomTipAmount] = useState('');
  const [customTipError, setCustomTipError] = useState('');

  // Get current order total from API pricing data
  const currentTotal = apiPricingData ? apiPricingData.total / 100 : total;

  // Always use fixed percentage values [10%, 15%, 20%]
  // Memoize tip values to prevent recalculation on every render
  const tipValues = useMemo(() => {
    const percentages = [10, 15, 20];

    return {
      tipA: `${percentages[0]}%`,
      tipAAmount: `${environment.currency}${(
        (currentTotal * percentages[0]) /
        100
      ).toFixed(2)}`,
      tipB: `${percentages[1]}%`,
      tipBAmount: `${environment.currency}${(
        (currentTotal * percentages[1]) /
        100
      ).toFixed(2)}`,
      tipC: `${percentages[2]}%`,
      tipCAmount: `${environment.currency}${(
        (currentTotal * percentages[2]) /
        100
      ).toFixed(2)}`,
    };
  }, [currentTotal, environment.currency]);

  // Use useCallback to prevent function recreation on every render
  const handleOpenCustomTip = useCallback(() => {
    setOpenCustomTipDialog(true);
    setCustomTipError('');
    setCustomTipAmount('');
  }, []);

  const handleCloseCustomTip = useCallback(() => {
    setOpenCustomTipDialog(false);
    setCustomTipAmount('');
    setCustomTipError('');
  }, []);

  const handleApplyCustomTip = useCallback(() => {
    const tipValue = parseFloat(customTipAmount);

    // Validation
    if (!customTipAmount || isNaN(tipValue)) {
      setCustomTipError('Please enter a valid amount');
      return;
    }

    if (tipValue < 0) {
      setCustomTipError('Tip amount cannot be negative');
      return;
    }

    // Convert dollars to cents since the system expects cents
    const tipValueInCents = Math.round(tipValue * 100);

    // Apply custom tip - pass the amount in cents directly to setTypeTip
    // This ensures the tip amount is available immediately when calculating
    setTypeTip('other', tipValueInCents);

    // Also update the parent's otherTip state if the function is provided
    if (setOtherTip) {
      setOtherTip(tipValueInCents);
    }

    handleCloseCustomTip();
  }, [customTipAmount, setTypeTip, setOtherTip, handleCloseCustomTip]);

  const handleTipAmountChange = useCallback((e) => {
    setCustomTipAmount(e.target.value);
    setCustomTipError('');
  }, []);

  const handleQuickTipClick = useCallback((amount) => {
    setCustomTipAmount(amount.toString());
    setCustomTipError('');
  }, []);

  return (
    <>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          justifyContent: 'space-between',
          flexDirection: 'row',
          gap: 1,
        }}
      >
        {tipValues.tipA && (
          <TipButton
            tipsValue={tipValues.tipA}
            displayAmount={tipValues.tipAAmount}
            tipTypeValue={'a'}
            {...{ typeTip, setTypeTip, environment }}
          />
        )}
        {tipValues.tipB && (
          <TipButton
            tipsValue={tipValues.tipB}
            displayAmount={tipValues.tipBAmount}
            tipTypeValue={'b'}
            {...{ typeTip, setTypeTip, environment }}
          />
        )}
        {tipValues.tipC && (
          <TipButton
            tipsValue={tipValues.tipC}
            displayAmount={tipValues.tipCAmount}
            tipTypeValue={'c'}
            {...{ typeTip, setTypeTip, environment }}
          />
        )}
        <TipButton
          tipsValue="Other"
          tipTypeValue={'other'}
          isOtherButton={true}
          displayAmount={
            typeTip === 'other' && otherTip != null && Number(otherTip) >= 0
              ? `${environment.currency}${(Number(otherTip) / 100).toFixed(2)}`
              : undefined
          }
          onClick={handleOpenCustomTip}
          {...{ typeTip, setTypeTip, environment }}
        />
      </Box>

      {/* Custom Tip Dialog */}
      <Dialog
        open={openCustomTipDialog}
        onClose={handleCloseCustomTip}
        maxWidth="xs"
        fullWidth
        transitionDuration={{ enter: 200, exit: 150 }}
        disableEscapeKeyDown={false}
        PaperProps={{
          style: {
            borderRadius: '20px',
            padding: '8px',
            boxShadow: '0px 20px 60px rgba(0, 0, 0, 0.15)',
            margin: '16px',
          },
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '24px 24px 16px 24px',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontWeight: 700,
              fontSize: '24px',
              color: '#172B4D',
            }}
          >
            Custom Tip
          </Typography>
          <IconButton
            onClick={handleCloseCustomTip}
            sx={{
              color: '#666666',
              '&:hover': {
                backgroundColor: '#F6F6F6',
              },
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ padding: '16px 24px 24px 24px' }}>
          <Typography
            sx={{
              fontFamily: 'Jost, sans-serif',
              fontSize: '14px',
              color: '#666666',
              marginBottom: '16px',
            }}
          >
            Enter your desired tip amount
          </Typography>

          <TextField
            autoFocus
            fullWidth
            type="number"
            value={customTipAmount}
            onChange={handleTipAmountChange}
            placeholder="0.00"
            error={!!customTipError}
            helperText={customTipError}
            InputProps={{
              startAdornment: (
                <Typography
                  sx={{
                    fontFamily: 'Jost, sans-serif',
                    fontWeight: 600,
                    fontSize: '18px',
                    color: '#172B4D',
                    marginRight: '8px',
                  }}
                >
                  {environment.currency}
                </Typography>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px',
                fontFamily: 'Jost, sans-serif',
                fontSize: '18px',
                fontWeight: 600,
                '& fieldset': {
                  borderColor: '#E6E6E6',
                  borderWidth: '2px',
                },
                '&:hover fieldset': {
                  borderColor: environment.mainColor,
                },
                '&.Mui-focused fieldset': {
                  borderColor: environment.mainColor,
                },
              },
              '& input[type=number]': {
                MozAppearance: 'textfield',
              },
              '& input[type=number]::-webkit-outer-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
              '& input[type=number]::-webkit-inner-spin-button': {
                WebkitAppearance: 'none',
                margin: 0,
              },
            }}
          />

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              marginTop: '16px',
              flexWrap: 'wrap',
            }}
          >
            {[1, 2, 5, 10].map((amount) => (
              <Button
                key={amount}
                onClick={() => handleQuickTipClick(amount)}
                sx={{
                  borderRadius: '8px',
                  padding: '8px 16px',
                  backgroundColor: '#F6F6F6',
                  color: '#172B4D',
                  fontFamily: 'Jost, sans-serif',
                  fontWeight: 600,
                  fontSize: '14px',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: `${environment.mainColor}20`,
                    color: environment.mainColor,
                  },
                }}
              >
                {environment.currency}
                {amount}
              </Button>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={{ padding: '0 24px 24px 24px', gap: 1 }}>
          <Button
            onClick={handleCloseCustomTip}
            sx={{
              flex: 1,
              borderRadius: '12px',
              padding: '14px',
              backgroundColor: '#F6F6F6',
              color: '#172B4D',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#E6E6E6',
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleApplyCustomTip}
            sx={{
              flex: 1,
              borderRadius: '12px',
              padding: '14px',
              backgroundColor: environment.mainColor,
              color: 'white',
              fontFamily: 'Jost, sans-serif',
              fontWeight: 600,
              fontSize: '16px',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: environment.mainColor,
                opacity: 0.9,
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TipsBox;
